import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import VirtualList from 'react-tiny-virtual-list';
import AutoSizer from 'react-virtualized-auto-sizer';

import Clubs from '../Clubs';
import { Paginator } from '../Paginator';

import { Button } from '@/components';
import Tags from '@/components/Tags';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePlayerTemplateMap } from '@/hooks/usePlayer';
import { useTeamStore } from '@/hooks/useTeamStore';
import { useTournament } from '@/hooks/useTournament';
import { TeamActionTypes } from '@/store';
import { JugFigura } from '@/types/fieldAssistance';
import { SelectValue } from '@/types/index';
import { VariantFieldAssistance } from '@/types/team';
import {
  EMPTY_TEMPLATE_PLAYER,
  PLAYER_POSITION_BY_ID,
  cn,
  formatMillionBalance,
  hashNameComponents,
} from '@/utils';

interface Props extends VariantFieldAssistance {
  players: JugFigura[];
  positionSelected: SelectValue;
}

export const BestOfMatchdayTable = ({ players, positionSelected }: Props) => {
  const vlistRef = useRef<VirtualList>(null);
  const { isMobile } = useIsDesktop();
  const playerByTemplate = usePlayerTemplateMap();

  const filteredPlayers = useMemo(() => {
    let filtered = players;

    if (positionSelected.value !== 0) {
      filtered = players.filter((p) => {
        const template = playerByTemplate.get(p.idJT);
        return template?.p === positionSelected.value;
      });
    }

    // Sort by score descending
    return filtered.sort((a, b) => b.valorReporte - a.valorReporte);
  }, [players, positionSelected, playerByTemplate]);

  useEffect(() => {
    if (!vlistRef.current) return;
    vlistRef.current.scrollTo(0);
  }, [positionSelected.value]);

  const [contentCurrentPage, setContentCurrentPage] = useState<JugFigura[]>([]);

  const handleContentCurrentPage = (players: JugFigura[]) => {
    players && setContentCurrentPage(players);
  };

  const itemsToRender =
    Array.isArray(contentCurrentPage) && contentCurrentPage.length > 0
      ? contentCurrentPage
      : filteredPlayers;

  if (!players || players.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-gdt-gray-2">No hay datos disponibles.</p>
      </div>
    );
  }

  if (isMobile) {
    return (
      filteredPlayers &&
      filteredPlayers.length > 0 && (
        <div className="h-screen mt-5 rounded-lg bg-white">
          <AutoSizer className="h-full lg:h-[1002px]">
            {({ height, width }) => {
              return (
                <VirtualList
                  ref={vlistRef}
                  height={height}
                  itemCount={filteredPlayers.length}
                  itemSize={83}
                  renderItem={({ index, style }) => {
                    const player = filteredPlayers[index];
                    if (player) {
                      return (
                        <div key={player.idJT} style={style}>
                          <ItemBestOfMatchday index={index} player={player} />
                        </div>
                      );
                    }
                  }}
                  width={width}
                />
              );
            }}
          </AutoSizer>
        </div>
      )
    );
  }

  return (
    <div className="flex flex-col h-full mt-10 ">
      <div className="flex-1 overflow-y-auto">
        {itemsToRender.map((player, index) => (
          <ItemBestOfMatchday key={player.idJT} index={index} player={player} />
        ))}
      </div>

      {filteredPlayers.length > 10 && (
        <div className="bg-white rounded-b-lg">
          <Paginator
            key={`best-of-matchday-${positionSelected.value}`}
            currentContentPage={handleContentCurrentPage}
            items={filteredPlayers}
          />
        </div>
      )}
    </div>
  );
};

interface ItemProps {
  player: JugFigura;
  index?: number;
}

const ItemBestOfMatchday = ({ player, index }: ItemProps) => {
  const { isMobile } = useIsDesktop();
  const { pathname } = useLocation();
  const { dispatch, state } = useTeamStore();
  const { tournamentMap } = useTournament();
  const playerByTemplate = usePlayerTemplateMap();

  const templatePlayer =
    playerByTemplate.get(player.idJT) || EMPTY_TEMPLATE_PLAYER;
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const club = teamsMap ? teamsMap.get(templatePlayer.c) : undefined;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const tournamentStatus = tournamentStatusMap
    ? tournamentStatusMap.get(templatePlayer.e)
    : undefined;

  const ifEmbedded = isMobile && state.selectedPlayerOnTeam;

  return (
    <div
      className={cn(
        'px-4 lg:px-[4rem] flex h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm bg-white',
        index === 0 && 'rounded-t-lg'
      )}
    >
      <div className="flex-1 flex gap-3 items-center">
        <figure className="w-[28px] h-[31px]">
          <Clubs
            clubId={club?.id}
            playerId={templatePlayer.id}
            type={'SHIRT'}
          />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex lg:items-center">
          <p className="text-xs lg:text-base font-bold text-gdt-gray">
            {templatePlayer.a}, {templatePlayer.n}
          </p>
          <p className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase">
            {PLAYER_POSITION_BY_ID[templatePlayer.p as 1 | 2 | 3 | 4]} -{' '}
            {club?.nombre}
          </p>
          <div className="flex items-center justify-normal space-x-3 lg:space-x-3">
            <p className="text-2xs lg:text-sm font-semibold text-gdt-gray-3">
              ${formatMillionBalance(templatePlayer.cot * 10000)}
            </p>
            <Tags.Alert statusId={templatePlayer.e}>
              <p className="lg:text-sm">{tournamentStatus?.nombre}</p>
            </Tags.Alert>
          </div>
        </div>
      </div>
      <div className="text-right flex items-center flex-col md:flex-row justify-end w-[100px] lg:max-w-[244px] lg:w-full font-bold text-xs text-gdt-gray-2">
        <p className="text-sm lg:text-base mr-4">
          {player.valorReporte} puntos
        </p>
        <Button
          className="underline-offset-8 text-xs lg:text-sm font-bold"
          href={
            ifEmbedded ? pathname : hashNameComponents.showPlayersListTransfer
          }
          type="link"
          variant="text"
          onClick={() => {
            dispatch({
              type: TeamActionTypes.SET_STATE,
              payload: {
                selectedPlayerOnList: templatePlayer,
                ...(ifEmbedded
                  ? { playerDetailsModalOpen: true }
                  : { showPlayersListTransfer: true }),
              },
            });
          }}
        >
          Agregar
        </Button>
      </div>
    </div>
  );
};
